/**
 * Shared chrome for the Model Explorer's rows — sub-model headers, field rows,
 * category rows, connector rows and transformation sub-rows.
 *
 * Restyled onto the design system 2026-08-30; see `okf/isr/e2e.md` § "The
 * expandable list". Three components (`SubModelItem`, `FieldItem`,
 * `ProductCategoryItem`) each carried their own near-copy of this class string, and
 * they had already drifted: `SubModelItem`'s header put the padding on the row while
 * the other two put it on an inner block, and `ProductCategoryItem`'s transformation
 * sub-row painted `bg-white` where `FieldItem`'s fraction sub-row painted `#F4F5F7`.
 * One definition is what stops the next drift.
 *
 * ## What changed, and why
 *
 * **The row wrapper is inert.** All three were `div role="button" onClick` with no
 * `tabIndex` and no key handler. Measured on one expanded sub-model with six
 * children: **15** elements with `role="button"` and no tab stop, and exactly **4**
 * focusable elements in the whole card (the chevron and three add-buttons). So the
 * tree announced itself as a pile of buttons and could not be operated by keyboard
 * at all. The select target is a real `<button>` now (`EXPLORER_SELECT_BTN`) and the
 * ⋮ is its sibling, not a `role="button"` nested inside another one.
 *
 * **Selected vs hover.** Selection was a 1px blue border and blue text, with no
 * hover state anywhere in the rail — `cursor-pointer` and nothing else. Selected now
 * takes the DS selection grammar (§ 51/§ 65), `accent` on `accent-tint`, the same
 * pair Dropdown / Menu / Tab already wear. Hover darkens the BORDER to `edge-strong`
 * rather than moving the ground: these rows sit on `surface-sunken` inside a
 * sub-model card, so a `surface-sunken` hover would have dissolved the row into its
 * own container.
 *
 * **Same 44px row.** The DS type tokens are 12px/16px where these labels rendered
 * 12px/18px, so `py-[3px]` → `py-1` holds the measured height exactly.
 *
 * Every rendered colour is unchanged or better: `grey-200` and `edge` are both
 * `#dadbdd`, `shades-of-grey-0` and `surface` are both `#ffffff`.
 */

/**
 * The row wrapper: chrome only, no interaction. `items-stretch` (was `items-center`)
 * is what lets the class colour bar span the row — see the note on it in `FieldItem`.
 */
export const EXPLORER_ROW =
  'tw-rounded-ctl tw-bg-surface tw-box-border tw-w-auto tw-flex tw-flex-row ' +
  'tw-items-stretch tw-justify-between tw-gap-3 tw-border tw-border-solid ' +
  'motion-safe:tw-transition-colors';

/** Resting / hover / selected, as one decision so the three states cannot drift apart. */
export const EXPLORER_ROW_STATE = (isSelected: boolean): string =>
  isSelected
    ? 'tw-border-accent tw-bg-accent-tint tw-text-accent'
    : 'tw-border-edge tw-text-ink hover:tw-border-edge-strong';

/**
 * The select target. `flex-1` so it covers everything the old row covered except the
 * ⋮ itself, and `min-w-0` so a long name wraps inside it instead of forcing the row
 * wider than the rail.
 */
export const EXPLORER_SELECT_BTN =
  'tw-flex-1 tw-min-w-0 tw-text-left tw-cursor-pointer tw-rounded-ctl';

/*
 * `BLUE_OUTLINE` ('!tw-border-accent !tw-text-accent') lived here until 2026-09-07 —
 * the documented deviation that kept every rail button blue at rest. Its own revisit
 * clause fired: with a selected row ALSO wearing accent (§ 51/§ 65 above), a rail with
 * N expanded sub-models showed 2N+3 accent surfaces and selection stopped reading.
 * Closed by the user's call ("colors are dominating"): Collapse All is `subtle`, the
 * per-card adders are plain DS `secondary`, and Create Sub model — the rail's ONE loud
 * control — keeps the accent outline INLINE in `DefineModel.tsx` (a filled primary was
 * tried and read too heavy). Accent otherwise belongs to the selected row.
 */
